import {isNavigationFailure, NavigationFailureType} from 'vue-router'

import {api} from 'shared/plugins/api'
import createQueryString from 'shared/utils/createQueryString'
import isFlatObjectEqual from 'shared/utils/isFlatObjectEqual'

export const useRouterHelpers = router => {
	async function pushQueryParams(queryParams) {
		const query = getNewQuery(queryParams)
		const error = await router.push({query})
		if (!error) return
		handleVueRouterError(error, queryParams)
	}

	async function replaceQueryParams(queryParams) {
		const query = getNewQuery(queryParams)
		if (isFlatObjectEqual(query, router.currentRoute.value.query)) return
		const error = await router.replace({query})
		if (!error) return
		handleVueRouterError(error, queryParams)
	}

	async function replace(location) {
		const error = await router.replace(location)
		if (!error) return
		handleVueRouterError(error)
	}

	async function push(location) {
		const error = await router.push(location)
		if (!error) return
		handleVueRouterError(error)
	}

	async function navigateToAcceptInviteOrOwnWorkspace() {
		try {
			const workspaceId = router.currentRoute.value.params.workspaceId
			if (!workspaceId) return push('/')
			await api.get(`workspace/can-user-join?workspaceId=` + workspaceId)
			push(`/workspaces/${workspaceId}/invites`)
		} catch (error) {
			push('/')
		}
	}

	function navigateToCompleteSignup(redirectUrl = router.currentRoute.value.fullPath) {
		// using sessionStorage instead of query strings to prevent adding untrusted redirect url
		if (sessionStorage) sessionStorage.redirectUrlAfterCompletingSignUp = redirectUrl
		return replace('/complete-signup')
	}

	async function navigateToWorkspaceSettings(
		settingsPageName = '',
		routeOptions = {},
		workspaceId,
		replaceHistory = false
	) {
		if (!workspaceId) {
			workspaceId = router.currentRoute.value.params.workspaceId
		}
		const path = `/workspaces/${workspaceId}/settings/${settingsPageName}`

		// in the extension, open the settings in a new tab
		if (import.meta.env.VUE_APP_TARGET === 'extension') {
			const queryString = createQueryString(routeOptions.query || {})
			return window.open(`${import.meta.env.VUE_APP_URL}${path}${queryString}`, '_blank')
		}

		routeOptions.query = {...routeOptions.query}
		return replaceHistory ? replace({...routeOptions, path}) : push({...routeOptions, path})
	}

	async function navigateToAccountSettings(settingsPageName = 'profile', routeOptions = {}) {
		const path = `/settings/account/${settingsPageName}`

		// in the extension, open the settings in a new tab
		if (import.meta.env.VUE_APP_TARGET === 'extension') {
			const queryString = createQueryString(routeOptions.query || {})
			return window.open(`${import.meta.env.VUE_APP_URL}${path}${queryString}`, '_blank')
		}

		replaceQueryParams({modal: null})
		push({...routeOptions, path})
	}

	// https://v3.router.vuejs.org/guide/advanced/navigation-failures.html#detecting-navigation-failures
	function handleVueRouterError(error, params) {
		// Ignore navigation errors, because so far all of them have been triggered by code put in place on purpose. Vue router's stance on throwing errors is... a bit questionable, to say the least. For an explanation see https://github.com/vuejs/vue-router/issues/2881#issuecomment-520554378.
		if (isNavigationFailure(error)) {
			if (import.meta.env.DEV) {
				// eslint-disable-next-line no-console
				console.warn('VueRouter navigation error ignored: ', error.message, params)
			}
			if (
				isNavigationFailure(error, NavigationFailureType.cancelled) ||
				isNavigationFailure(error, NavigationFailureType.aborted)
			) {
				router.go()
			}
			return
		}
		throw error
	}

	function getNewQuery(queryParams = {}) {
		const query = {...router.currentRoute.value.query, ...queryParams}

		Object.entries(queryParams).forEach(([key, value]) => {
			if (value === null || value === undefined) {
				delete query[key]
			}
		})

		return query
	}

	return {
		pushQueryParams,
		replaceQueryParams,
		replace,
		push,
		navigateToAcceptInviteOrOwnWorkspace,
		navigateToCompleteSignup,
		navigateToWorkspaceSettings,
		navigateToAccountSettings,
	}
}
